import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Search, Download, Eye, Edit, Trash2 } from "lucide-react";
import AppLayout from "../layouts/AppLayout";
import ResponseModal from "../components/dashboard/ModalResponse";
import RegistrationFormModal from "../components/dashboard/RegistrationFormModal";

const baseUrl =
  process.env.REACT_APP_PUBLIC_API_URL

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const ResponsePage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [responses, setResponses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isNewEntryModalOpen, setIsNewEntryModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const fetchResponses = useCallback((opts = {}) => {
    const limit = opts.pageSize ?? pageSize;
    const offset = ((opts.page ?? page) - 1) * limit;
    setLoading(true);
    axios
      .get(`${baseUrl}/list`, { params: { limit, offset } })
      .then((response) => {
        const res = response.data;
        if (res.success && Array.isArray(res.registrations)) {
          setResponses(
            res.registrations.map((r) => ({
              id: r.registrationId,
              user: r.user,
              age: r.age,
              gender: r.gender,
              region: r.region,
              valueChain: r.valueChainInterest,
              education: r.highestEducation,
              date: r.date,
              status: r.status,
              ...r,
            }))
          );
          setTotal(res.total ?? 0);
        }
      })
      .catch((error) => console.error("Error fetching responses:", error))
      .finally(() => setLoading(false));
  }, [page, pageSize]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  const filteredResponses = useMemo(() => {
    if (!searchTerm.trim()) return responses;
    const term = searchTerm.trim().toLowerCase();
    return responses.filter(
      (res) =>
        (res.user && res.user.toLowerCase().includes(term)) ||
        (res.id && res.id.toLowerCase().includes(term))
    );
  }, [responses, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize, total);
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setPage(1);
  };


  const handleViewClick = (response) => {
    setSelectedResponse(response);
    setIsModalOpen(true);
  };


  const FULL_CSV_COLUMNS = [
    "registrationId", "firstName", "lastName", "middleName", "dateOfBirth", "gender", "ageGroup", "idNumber", "disability",
    "region", "district", "community", "phoneNumber", "email", "whatsapp",
    "highestEducation", "enrollmentStatus", "completionStatus", "mainOccupation",
    "interestInAgriculture", "valueChainInterest", "currentEngagement",
    "landAccess", "financeAccess", "toolsTechnologyAccess", "mobilephoneAccess",
    "safetyPerception", "riskHarmful", "reportIssues", "safeguardinginfo",
    "created_at", "age", "date",
  ];

  const exportToCsv = () => {
    const escapeCsv = (val) => {
      const s = val == null ? "" : String(val).trim();
      if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    axios
      .get(`${baseUrl}/list`, { params: { limit: 10000, full: true } })
      .then((response) => {
        const res = response.data;
        if (!res.success || !Array.isArray(res.registrations)) return;
        const list = res.registrations;
        const columns = list.length > 0
          ? (FULL_CSV_COLUMNS.filter((c) => list[0][c] !== undefined).length
            ? FULL_CSV_COLUMNS.filter((c) => list[0][c] !== undefined)
            : Object.keys(list[0]))
          : FULL_CSV_COLUMNS;
        const headerRow = columns.map(escapeCsv).join(",");
        const dataRows = list.map((row) =>
          columns.map((col) => escapeCsv(row[col])).join(",")
        );
        const csv = [headerRow, ...dataRows].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `survey-responses-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => console.error("Export failed:", err));
  };

  return (
    <AppLayout>
      <div className="p-8 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Survey Responses
              </h1>
              <p className="text-gray-500 text-sm">
                Manage and review all incoming participant data
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={exportToCsv}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all"
              >
                <Download size={18} />
                Export CSV
              </button>
              <button
                onClick={() => setIsNewEntryModalOpen(true)}
                className="px-4 py-2 bg-green-700 text-white rounded-xl text-sm font-semibold hover:bg-green-800 transition-all shadow-sm"
              >
                + New Entry
              </button>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white p-4 rounded-t-[1.5rem] border border-gray-100 border-b-0 flex items-center">
            <div className="relative flex-1 max-w-md">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by name or ID..."
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Table Content */}
          <div className="bg-white rounded-b-[1.5rem] shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Full Name
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Age
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Gender
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Region
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Value Chain
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Education
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {loading ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-12 text-center text-sm text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : filteredResponses.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-12 text-center text-sm text-gray-500">
                        No responses yet.
                      </td>
                    </tr>
                  ) : (
                    filteredResponses.map((res, index) => (
                    <tr
                      key={res.id || index}
                      className="hover:bg-gray-50/50 transition-colors group"
                    >
                      <td className="px-6 py-4 text-sm font-bold text-gray-900">
                        {res.id}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-700">
                        {res.user}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {res.age}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {res.gender}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {res.region}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {res.valueChain}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {res.education}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {res.date}
                      </td>

                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleViewClick(res)}
                            className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-blue-600"
                          >
                            <Eye size={16} />
                          </button>
                          <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-green-600">
                            <Edit size={16} />
                          </button>
                          <button className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-red-600">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm text-gray-500">
                  {loading
                    ? "Loading..."
                    : total === 0
                    ? "No entries"
                    : `Showing ${startRow} to ${endRow} of ${total} entries`}
                </span>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Rows per page</span>
                  <select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={!hasPrev}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500 px-2">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!hasNext}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
        <ResponseModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={selectedResponse}
        />
        <RegistrationFormModal
          isOpen={isNewEntryModalOpen}
          onClose={() => setIsNewEntryModalOpen(false)}
          onSuccess={() => {
            setIsNewEntryModalOpen(false);
            fetchResponses();
          }}
        />
      </div>
    </AppLayout>
  );
};

export default ResponsePage;
